import React, { useEffect, useState, useId } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Car, Store, Shield, ArrowRight, Layers, Loader2, UtensilsCrossed, ImageIcon, CheckCircle2, XCircle, MessageCircle, Eye, Clock, AlertCircle, Inbox, Check, X, FileText, BarChart3, Receipt, LogOut, UserIcon, ChefHat } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Modal } from '../../components/ui/Modal';
import { AnimatePresence, motion } from 'framer-motion';
import { useIdleLogout } from '../../lib/useIdleLogout';
interface RestaurantApplication {
  id: string;
  owner_name: string;
  restaurant_name: string;
  whatsapp: string;
  social_handle: string | null;
  business_type: string;
  uses_whatsapp_orders: boolean;
  has_digital_menu: boolean;
  main_goal: string | null;
  status: 'pending' | 'contacted' | 'approved' | 'rejected';
  created_at: string;
}
const ENROLLMENT_OPTIONS = [{
  title: 'Conductor',
  description: 'Registrar conductor, crear o reutilizar vehículo y dejar accesos listos',
  route: '/admin/drivers/new',
  icon: Car,
  accent: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
  iconBg: 'bg-blue-500/15',
  btnClass: 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
}, {
  title: 'Subir restaurante',
  description: 'Registrar restaurante, crear acceso inicial y dejarlo listo para operar',
  route: '/admin/enrollment/restaurant',
  icon: Store,
  accent: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
  iconBg: 'bg-purple-500/15',
  btnClass: 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
}, {
  title: 'Generar Recibo',
  description: 'Crear recibos de pago profesionales para clientes, imprimir o compartir por WhatsApp',
  route: '/admin/enrollment/receipts',
  icon: Receipt,
  accent: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
  iconBg: 'bg-emerald-500/15',
  btnClass: 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
}, {
  title: 'Admin de NOIZA',
  description: 'Crear usuario interno de NOIZA y asignar permisos de plataforma',
  route: '/admin/enrollment/platform-admin',
  icon: Shield,
  accent: 'bg-[#006847]/10 border-[#006847]/20 text-[#00a870]',
  iconBg: 'bg-[#006847]/20',
  btnClass: 'bg-[#006847]/20 text-[#00a870] hover:bg-[#006847]/30'
}, {
  title: 'Actualizar menú',
  description: 'Agregar categorías, productos u opciones a un restaurante existente sin recrearlo',
  route: '/admin/enrollment/menu-update',
  icon: UtensilsCrossed,
  accent: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
  iconBg: 'bg-amber-500/15',
  btnClass: 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30',
  btnLabel: 'Iniciar actualización'
}, {
  title: 'Subir imágenes del menú',
  description: 'Relaciona imágenes con productos del menú usando el nombre del archivo',
  route: '/admin/enrollment/menu-images',
  icon: ImageIcon,
  accent: 'bg-teal-500/10 border-teal-500/20 text-teal-400',
  iconBg: 'bg-teal-500/15',
  btnClass: 'bg-teal-500/20 text-teal-400 hover:bg-teal-500/30',
  btnLabel: 'Abrir módulo'
}, {
  title: 'Métricas NOIZA',
  description: 'Revisa métricas globales, crecimiento y operación de NOIZA',
  route: '/admin/enrollment/metrics',
  icon: BarChart3,
  accent: 'bg-rose-500/10 border-rose-500/20 text-rose-400',
  iconBg: 'bg-rose-500/15',
  btnClass: 'bg-rose-500/20 text-rose-400 hover:bg-rose-500/30',
  btnLabel: 'Ver métricas'
}];
export function EnrollmentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // --- Access Guard State ---
  const [guardState, setGuardState] = useState<'loading' | 'authorized' | 'redirecting'>('loading');
  // --- Idle Logout ---
  const {
    showWarning: showIdleWarning
  } = useIdleLogout({
    enabled: guardState === 'authorized',
    timeoutMs: 900000,
    warningBeforeMs: 60000,
    redirectTo: '/admin/login'
  });
  // --- Active Restaurants State ---
  const [restaurants, setRestaurants] = useState<{
    id: string;
    name: string;
    slug: string;
    logo_url: string | null;
  }[]>([]);
  const [restLoading, setRestLoading] = useState(true);
  // --- Applications State ---
  const [applications, setApplications] = useState<RestaurantApplication[]>([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [appsError, setAppsError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'contacted' | 'approved' | 'rejected'>('all');
  const [selectedApp, setSelectedApp] = useState<RestaurantApplication | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [appsModalOpen, setAppsModalOpen] = useState(false);
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [sessionUserId, setSessionUserId] = useState<string | null>(null);
  const [showSessionInfo, setShowSessionInfo] = useState(false);
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login', {
      replace: true
    });
  };
  const fetchApplications = async () => {
    setAppsLoading(true);
    setAppsError(null);
    try {
      const {
        data,
        error
      } = await supabase.from('restaurant_applications').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;
      setApplications(data || []);
    } catch (err: any) {
      console.error('Error fetching applications:', err);
      setAppsError(err.message);
    } finally {
      setAppsLoading(false);
    }
  };
  // ─── Access Guard ─────────────────────────────────────────────
  useEffect(() => {
    async function guard() {
      try {
        // 1. Check authenticated user
        const {
          data: {
            user
          },
          error: userError
        } = await supabase.auth.getUser();
        if (userError || !user) {
          console.log('[EnrollmentPage] No authenticated user — redirecting to /admin/login');
          setGuardState('redirecting');
          navigate('/admin/login', {
            state: {
              from: location.pathname
            },
            replace: true
          });
          return;
        }
        // 2. Check platform_admins table first (primary source)
        let accessRole: string | null = null;
        const {
          data: platformData,
          error: platformError
        } = await supabase.from('platform_admins').select('role').eq('user_id', user.id).eq('is_active', true).maybeSingle();
        if (!platformError && platformData && (platformData.role === 'super_admin' || platformData.role === 'platform_admin')) {
          accessRole = platformData.role;
        }
        // 3. Fallback: check restaurant_users table
        if (!accessRole) {
          const {
            data: roleData,
            error: roleError
          } = await supabase.from('restaurant_users').select('role').eq('user_id', user.id).eq('is_active', true).in('role', ['super_admin', 'platform_admin']).limit(1).maybeSingle();
          if (roleError) {
            console.error('[EnrollmentPage] Role check error:', roleError);
          }
          if (roleData) {
            accessRole = roleData.role;
          }
        }
        if (!accessRole) {
          console.log('[EnrollmentPage] User lacks required role — redirecting to /');
          setGuardState('redirecting');
          navigate('/', {
            replace: true
          });
          return;
        }
        console.log('[EnrollmentPage] Access granted:', {
          email: user.email,
          role: accessRole
        });
        setSessionEmail(user.email || null);
        setSessionUserId(user.id);
        setGuardState('authorized');
      } catch (err) {
        console.error('[EnrollmentPage] Guard error:', err);
        setGuardState('redirecting');
        navigate('/admin/login', {
          replace: true
        });
      }
    }
    guard();
  }, [navigate, location.pathname]);
  const fetchRestaurants = async () => {
    setRestLoading(true);
    try {
      const {
        data,
        error
      } = await supabase.from('restaurants').select('id, name, slug, logo_url').eq('is_active', true).order('name', {
        ascending: true
      });
      if (error) throw error;
      setRestaurants(data || []);
    } catch (err: any) {
      console.error('Error fetching restaurants:', err);
    } finally {
      setRestLoading(false);
    }
  };
  useEffect(() => {
    if (guardState === 'authorized') {
      fetchApplications();
      fetchRestaurants();
    }
  }, [guardState]);
  const updateStatus = async (id: string, newStatus: 'pending' | 'contacted' | 'approved' | 'rejected') => {
    setUpdatingId(id);
    try {
      const {
        error
      } = await supabase.from('restaurant_applications').update({
        status: newStatus
      }).eq('id', id);
      if (error) throw error;
      setToast({
        message: 'Estado actualizado correctamente',
        type: 'success'
      });
      await fetchApplications();
      if (selectedApp && selectedApp.id === id) {
        setSelectedApp({
          ...selectedApp,
          status: newStatus
        });
      }
    } catch (err: any) {
      console.error('Error updating status:', err);
      setToast({
        message: 'Error al actualizar estado',
        type: 'error'
      });
    } finally {
      setUpdatingId(null);
      setTimeout(() => setToast(null), 3000);
    }
  };
  const handleWhatsApp = (whatsapp: string) => {
    const cleanNumber = whatsapp.replace(/\D/g, '');
    if (cleanNumber) {
      window.open(`https://wa.me/${cleanNumber}`, '_blank');
    }
  };
  const filteredApps = applications.filter(app => statusFilter === 'all' || app.status === statusFilter);
  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    contacted: applications.filter(a => a.status === 'contacted').length,
    approved: applications.filter(a => a.status === 'approved').length,
    rejected: applications.filter(a => a.status === 'rejected').length
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20" data-id="element-2528">
            Pendiente
          </span>;
      case 'contacted':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-blue-500/10 text-blue-500 border border-blue-500/20" data-id="element-2529">
            Contactado
          </span>;
      case 'approved':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" data-id="element-2530">
            Aprobado
          </span>;
      case 'rejected':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-500/10 text-red-500 border border-red-500/20" data-id="element-2531">
            Rechazado
          </span>;
      default:
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-gray-500/10 text-gray-400 border border-gray-500/20" data-id="element-2532">
            {status}
          </span>;
    }
  };
  if (guardState !== 'authorized') {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-2533">
        <Loader2 className="w-8 h-8 text-[#006847] animate-spin" data-id="element-2534" />
      </div>;
  }
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans" data-id="element-2535">
      {/* Idle Warning Banner */}
      <AnimatePresence data-id="element-2536">
        {showIdleWarning && <motion.div initial={{
        y: -60,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: -60,
        opacity: 0
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed top-0 left-0 right-0 z-[70] flex items-center justify-center px-4 py-3 bg-amber-500/90 text-[#0B0B0B]" data-id="element-2537">
            <Clock className="w-4 h-4 mr-2 shrink-0" data-id="element-2538" />
            <span className="text-sm font-bold" data-id="element-2539">
              Por seguridad, tu sesión se cerrará pronto por inactividad.
            </span>
          </motion.div>}
      </AnimatePresence>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 space-y-10" data-id="element-2540">
        {/* Header */}
        <div className="flex items-center justify-between gap-4" data-id="element-2541">
          <div className="flex items-center gap-4" data-id="element-2542">
            <div className="w-14 h-14 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center" data-id="element-2543">
              <Layers className="w-7 h-7 text-[#006847]" data-id="element-2544" />
            </div>
            <div data-id="element-2545">
              <h1 className="text-3xl font-black tracking-tight" data-id="element-2546">
                NOIZA Enrollment
              </h1>
              <p className="text-white/40 text-sm mt-1" data-id="element-2547">
                Gestiona los diferentes tipos de registro y acceso dentro de
                NOIZA
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0" data-id="element-2548">
            {/* Session Info Button */}
            <div className="relative" data-id="element-2549">
              <button onClick={() => setShowSessionInfo(!showSessionInfo)} className="flex items-center gap-2 px-3 py-2.5 bg-[#141414] border border-white/10 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:border-white/20 transition-colors" data-id="element-2550">
                <UserIcon className="w-4 h-4" data-id="element-2551" />
                <span className="hidden sm:inline max-w-[160px] truncate" data-id="element-2552">
                  {sessionEmail || 'Usuario'}
                </span>
              </button>

              {/* Session Info Dropdown */}
              <AnimatePresence data-id="element-2553">
                {showSessionInfo && <motion.div initial={{
                opacity: 0,
                y: -4,
                scale: 0.95
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -4,
                scale: 0.95
              }} transition={{
                duration: 0.15
              }} className="absolute right-0 top-full mt-2 w-72 bg-[#141414] border border-white/10 rounded-2xl p-4 shadow-2xl z-50" data-id="element-2554">
                    <div className="space-y-3" data-id="element-2555">
                      <div data-id="element-2556">
                        <p className="text-[10px] uppercase tracking-wider text-white/30 font-bold mb-1" data-id="element-2557">
                          Sesión activa
                        </p>
                        <p className="text-sm font-bold text-white break-all" data-id="element-2558">
                          {sessionEmail || '—'}
                        </p>
                      </div>
                      {sessionUserId && <div data-id="element-2559">
                          <p className="text-[10px] uppercase tracking-wider text-white/30 font-bold mb-1" data-id="element-2560">
                            User ID
                          </p>
                          <p className="text-[11px] font-mono text-white/50 break-all" data-id="element-2561">
                            {sessionUserId}
                          </p>
                        </div>}
                      <div className="pt-2 border-t border-white/5" data-id="element-2562">
                        <button onClick={handleSignOut} className="w-full flex items-center justify-center gap-2 py-2.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-xl text-sm font-bold transition-colors" data-id="element-2563">
                          <LogOut className="w-4 h-4" data-id="element-2564" />
                          Cerrar sesión
                        </button>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Logout Button */}
            <button onClick={handleSignOut} className="p-2.5 bg-[#141414] border border-white/10 rounded-xl text-white/50 hover:text-red-400 hover:border-red-500/20 transition-colors" title="Cerrar sesión" data-id="element-2565">
              <LogOut className="w-5 h-5" data-id="element-2566" />
            </button>
          </div>
        </div>

        {/* Question */}
        <p className="text-white/50 text-lg font-medium" data-id="element-2567">
          ¿Qué deseas registrar hoy?
        </p>

        {/* Cards */}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2568">
          {ENROLLMENT_OPTIONS.map(opt => {
          const Icon = opt.icon;
          return <div key={opt.route} className="bg-[#141414] border border-white/10 rounded-2xl p-6 flex flex-col justify-between gap-6 hover:border-white/20 transition-colors" data-id="element-2569">
                <div className="space-y-4" data-id="element-2570">
                  <div className={`w-12 h-12 ${opt.iconBg} rounded-xl flex items-center justify-center`} data-id="element-2571">
                    <Icon className="w-6 h-6" data-id="element-2572" />
                  </div>
                  <div data-id="element-2573">
                    <h2 className="text-lg font-bold text-white" data-id="element-2574">
                      {opt.title}
                    </h2>
                    <p className="text-sm text-white/40 mt-1 leading-relaxed" data-id="element-2575">
                      {opt.description}
                    </p>
                  </div>
                </div>

                <button onClick={() => navigate(opt.route)} className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors ${opt.btnClass}`} data-id="element-2576">
                  {(opt as any).btnLabel || 'Iniciar enrollment'}
                  <ArrowRight className="w-4 h-4" data-id="element-2577" />
                </button>
              </div>;
        })}
        </div>

        {/* ═══ COMPACT APPLICATIONS CARD ═══ */}
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 flex flex-col justify-between gap-6 hover:border-white/20 transition-colors" data-id="element-2578">
          <div className="space-y-4" data-id="element-2579">
            <div className="flex items-start justify-between" data-id="element-2580">
              <div className="w-12 h-12 bg-orange-500/15 rounded-xl flex items-center justify-center" data-id="element-2581">
                <FileText className="w-6 h-6 text-orange-400" data-id="element-2582" />
              </div>
              {stats.pending > 0 && <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20 animate-pulse" data-id="element-2583">
                  {stats.pending} pendiente{stats.pending !== 1 ? 's' : ''}
                </span>}
            </div>
            <div data-id="element-2584">
              <h2 className="text-lg font-bold text-white" data-id="element-2585">
                Aplicaciones de restaurantes
              </h2>
              <p className="text-sm text-white/40 mt-1 leading-relaxed" data-id="element-2586">
                Revisa las solicitudes de ingreso a NOIZA
              </p>
            </div>

            {/* Compact Stats Row */}
            <div className="flex flex-wrap gap-3 pt-2" data-id="element-2587">
              <div className="flex items-center gap-1.5" data-id="element-2588">
                <div className="w-2 h-2 rounded-full bg-white/30" data-id="element-2589" />
                <span className="text-xs text-white/50" data-id="element-2590">
                  <span className="font-bold text-white" data-id="element-2591">{stats.total}</span>{' '}
                  total
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-2592">
                <div className="w-2 h-2 rounded-full bg-amber-500" data-id="element-2593" />
                <span className="text-xs text-white/50" data-id="element-2594">
                  <span className="font-bold text-amber-500" data-id="element-2595">
                    {stats.pending}
                  </span>{' '}
                  pendientes
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-2596">
                <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-2597" />
                <span className="text-xs text-white/50" data-id="element-2598">
                  <span className="font-bold text-blue-500" data-id="element-2599">
                    {stats.contacted}
                  </span>{' '}
                  contactados
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-2600">
                <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-2601" />
                <span className="text-xs text-white/50" data-id="element-2602">
                  <span className="font-bold text-emerald-500" data-id="element-2603">
                    {stats.approved}
                  </span>{' '}
                  aprobados
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-2604">
                <div className="w-2 h-2 rounded-full bg-red-500" data-id="element-2605" />
                <span className="text-xs text-white/50" data-id="element-2606">
                  <span className="font-bold text-red-500" data-id="element-2607">
                    {stats.rejected}
                  </span>{' '}
                  rechazados
                </span>
              </div>
            </div>
          </div>

          <button onClick={() => setAppsModalOpen(true)} className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors bg-orange-500/20 text-orange-400 hover:bg-orange-500/30" data-id="element-2608">
            Ver aplicaciones
            <ArrowRight className="w-4 h-4" data-id="element-2609" />
          </button>
        </div>

        {/* ═══ ACCESO RÁPIDO KDS ═══ */}
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-colors" data-id="element-2610">
          <div className="space-y-4" data-id="element-2611">
            <div className="flex items-start justify-between" data-id="element-2612">
              <div className="flex items-center gap-3" data-id="element-2613">
                <div className="w-12 h-12 bg-cyan-500/15 rounded-xl flex items-center justify-center" data-id="element-2614">
                  <ChefHat className="w-6 h-6 text-cyan-400" data-id="element-2615" />
                </div>
                <div data-id="element-2616">
                  <h2 className="text-lg font-bold text-white" data-id="element-2617">
                    Acceso rápido KDS
                  </h2>
                  <p className="text-sm text-white/40 mt-0.5" data-id="element-2618">
                    Ir directo a la cocina de cualquier restaurante
                  </p>
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20" data-id="element-2619">
                {restaurants.length} activos
              </span>
            </div>

            {restLoading ? <div className="flex items-center justify-center py-6" data-id="element-2620">
                <Loader2 className="w-6 h-6 text-white/20 animate-spin" data-id="element-2621" />
              </div> : restaurants.length === 0 ? <p className="text-white/30 text-sm text-center py-4" data-id="element-2622">
                No hay restaurantes activos
              </p> : <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 pt-2" data-id="element-2623">
                {restaurants.map(r => <button key={r.id} onClick={() => navigate(`/admin/${r.slug}/kds`)} className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-xl hover:border-cyan-500/30 hover:bg-cyan-500/5 transition-colors text-left group" data-id="element-2624">
                    <div className="w-10 h-10 rounded-lg bg-white/10 overflow-hidden shrink-0 flex items-center justify-center" data-id="element-2625">
                      {r.logo_url ? <img src={r.logo_url} alt={r.name} className="w-full h-full object-cover" data-id="element-2626" /> : <Store className="w-5 h-5 text-white/30" data-id="element-2627" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-2628">
                      <div className="font-bold text-white text-sm truncate group-hover:text-cyan-300 transition-colors" data-id="element-2629">
                        {r.name}
                      </div>
                      <div className="text-xs text-white/30 truncate" data-id="element-2630">
                        {r.slug}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-cyan-400 transition-colors shrink-0" data-id="element-2631" />
                  </button>)}
              </div>}
          </div>
        </div>

        {/* ═══ DOCUMENTOS LEGALES ═══ */}
        <div className="pt-4 border-t border-white/10" data-id="element-2632">
          <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-3" data-id="element-2633">
            Documentos legales
          </p>
          <div className="flex flex-wrap gap-3" data-id="element-2634">
            <a href="/terminos-restaurantes" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#141414] border border-white/10 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:border-white/20 transition-colors" data-id="element-2635">
              <FileText className="w-4 h-4 text-[#006847]" data-id="element-2636" />
              T&C Restaurantes
              <ArrowRight className="w-3.5 h-3.5 text-white/30" data-id="element-2637" />
            </a>
            <a href="/terminos-comensales" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#141414] border border-white/10 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:border-white/20 transition-colors" data-id="element-2638">
              <FileText className="w-4 h-4 text-blue-400" data-id="element-2639" />
              T&C Comensales
              <ArrowRight className="w-3.5 h-3.5 text-white/30" data-id="element-2640" />
            </a>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ═══ FULL-SCREEN APPLICATIONS MANAGER MODAL ═══ */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <AnimatePresence data-id="element-2641">
        {appsModalOpen && <motion.div className="fixed inset-0 z-50 flex items-stretch justify-end" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} data-id="element-2642">
            {/* Backdrop */}
            <motion.div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setAppsModalOpen(false)} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-2643" />

            {/* Slide-over Panel */}
            <motion.div className="relative w-full sm:w-[640px] lg:w-[780px] h-full bg-[#0B0B0B] border-l border-white/10 shadow-2xl flex flex-col overflow-hidden" initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} data-id="element-2644">
              {/* Panel Header */}
              <div className="shrink-0 px-6 py-5 border-b border-white/10 bg-[#0B0B0B]" data-id="element-2645">
                <div className="flex items-center justify-between" data-id="element-2646">
                  <div className="flex items-center gap-3" data-id="element-2647">
                    <div className="w-10 h-10 bg-orange-500/15 rounded-xl flex items-center justify-center" data-id="element-2648">
                      <FileText className="w-5 h-5 text-orange-400" data-id="element-2649" />
                    </div>
                    <div data-id="element-2650">
                      <h2 className="text-xl font-bold text-white" data-id="element-2651">
                        Aplicaciones de restaurantes
                      </h2>
                      <p className="text-white/40 text-xs mt-0.5" data-id="element-2652">
                        Centro de control de solicitudes
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setAppsModalOpen(false)} className="p-2 -mr-2 rounded-xl hover:bg-white/10 text-white/60 hover:text-white transition-colors" data-id="element-2653">
                    <X className="w-6 h-6" data-id="element-2654" />
                  </button>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-5 gap-3 mt-5" data-id="element-2655">
                  <div className="bg-[#141414] border border-white/10 rounded-xl p-3 text-center" data-id="element-2656">
                    <span className="text-white/40 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-2657">
                      Total
                    </span>
                    <span className="text-xl font-black text-white" data-id="element-2658">
                      {stats.total}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-amber-500/20 rounded-xl p-3 text-center" data-id="element-2659">
                    <span className="text-amber-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-2660">
                      Pendientes
                    </span>
                    <span className="text-xl font-black text-amber-500" data-id="element-2661">
                      {stats.pending}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-blue-500/20 rounded-xl p-3 text-center" data-id="element-2662">
                    <span className="text-blue-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-2663">
                      Contactados
                    </span>
                    <span className="text-xl font-black text-blue-500" data-id="element-2664">
                      {stats.contacted}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-emerald-500/20 rounded-xl p-3 text-center" data-id="element-2665">
                    <span className="text-emerald-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-2666">
                      Aprobados
                    </span>
                    <span className="text-xl font-black text-emerald-500" data-id="element-2667">
                      {stats.approved}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-red-500/20 rounded-xl p-3 text-center" data-id="element-2668">
                    <span className="text-red-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-2669">
                      Rechazados
                    </span>
                    <span className="text-xl font-black text-red-500" data-id="element-2670">
                      {stats.rejected}
                    </span>
                  </div>
                </div>

                {/* Filter Tabs */}
                <div className="flex overflow-x-auto hide-scrollbar gap-2 mt-4 pb-1" data-id="element-2671">
                  {[{
                id: 'all',
                label: 'Todos'
              }, {
                id: 'pending',
                label: 'Pendientes'
              }, {
                id: 'contacted',
                label: 'Contactados'
              }, {
                id: 'approved',
                label: 'Aprobados'
              }, {
                id: 'rejected',
                label: 'Rechazados'
              }].map(tab => <button key={tab.id} onClick={() => setStatusFilter(tab.id as any)} className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${statusFilter === tab.id ? 'bg-white text-[#0B0B0B]' : 'bg-[#141414] text-white/60 border border-white/10 hover:bg-white/5 hover:text-white'}`} data-id="element-2672">
                      {tab.label}
                    </button>)}
                </div>
              </div>

              {/* Panel Content — Scrollable */}
              <div className="flex-1 overflow-y-auto px-6 py-5" data-id="element-2673">
                {appsLoading ? <div className="space-y-4" data-id="element-2674">
                    {[1, 2, 3].map(i => <div key={i} className="h-24 bg-[#141414] rounded-2xl border border-white/5 animate-pulse" data-id="element-2675" />)}
                  </div> : appsError ? <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 text-center" data-id="element-2676">
                    <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" data-id="element-2677" />
                    <h3 className="text-lg font-bold text-white mb-1" data-id="element-2678">
                      Error al cargar aplicaciones
                    </h3>
                    <p className="text-white/60 text-sm mb-4" data-id="element-2679">{appsError}</p>
                    <button onClick={fetchApplications} className="px-6 py-2 bg-red-500/20 text-red-400 rounded-xl font-bold text-sm hover:bg-red-500/30 transition-colors" data-id="element-2680">
                      Reintentar
                    </button>
                  </div> : filteredApps.length === 0 ? <div className="bg-[#141414] border border-white/10 rounded-2xl p-12 text-center" data-id="element-2681">
                    <Inbox className="w-12 h-12 text-white/20 mx-auto mb-4" data-id="element-2682" />
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-2683">
                      Aún no hay aplicaciones
                    </h3>
                    <p className="text-white/50 text-sm" data-id="element-2684">
                      Cuando un restaurante aplique a NOIZA, aparecerá aquí.
                    </p>
                  </div> : <div className="space-y-4" data-id="element-2685">
                    {filteredApps.map(app => <div key={app.id} className="bg-[#141414] border border-white/10 rounded-2xl p-5 hover:border-white/20 transition-colors relative overflow-hidden" data-id="element-2686">
                        {updatingId === app.id && <div className="absolute inset-0 bg-[#141414]/80 backdrop-blur-sm z-10 flex items-center justify-center" data-id="element-2687">
                            <Loader2 className="w-6 h-6 text-white animate-spin" data-id="element-2688" />
                          </div>}

                        <div className="flex flex-col gap-4" data-id="element-2689">
                          {/* Row 1: Name + Status + Date */}
                          <div className="flex items-start justify-between gap-3" data-id="element-2690">
                            <div data-id="element-2691">
                              <div className="flex items-center gap-2 mb-1" data-id="element-2692">
                                <h3 className="font-bold text-lg text-white" data-id="element-2693">
                                  {app.restaurant_name}
                                </h3>
                                {getStatusBadge(app.status)}
                              </div>
                              <p className="text-sm text-white/60" data-id="element-2694">
                                {app.owner_name} • {app.business_type}
                              </p>
                            </div>
                            <p className="text-xs text-white/40 shrink-0" data-id="element-2695">
                              {new Date(app.created_at).toLocaleDateString('es-CR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                            </p>
                          </div>

                          {/* Row 2: Quick Info */}
                          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm" data-id="element-2696">
                            <div className="flex items-center gap-2" data-id="element-2697">
                              <span className="text-white/40" data-id="element-2698">WhatsApp:</span>
                              <span className="text-white font-medium" data-id="element-2699">
                                {app.whatsapp}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-2700">
                              <span className="text-white/40" data-id="element-2701">
                                Menú digital:
                              </span>
                              <span className="text-white font-medium" data-id="element-2702">
                                {app.has_digital_menu ? 'Sí' : 'No'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-2703">
                              <span className="text-white/40" data-id="element-2704">Pedidos WA:</span>
                              <span className="text-white font-medium" data-id="element-2705">
                                {app.uses_whatsapp_orders ? 'Sí' : 'No'}
                              </span>
                            </div>
                          </div>

                          {/* Row 3: Actions */}
                          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/5" data-id="element-2706">
                            <button onClick={() => {
                      setSelectedApp(app);
                      setDetailOpen(true);
                    }} className="px-3 py-2 bg-white/5 hover:bg-white/10 text-white text-sm font-bold rounded-xl transition-colors flex items-center gap-1.5" data-id="element-2707">
                              <Eye className="w-4 h-4" data-id="element-2708" /> Ver detalle
                            </button>
                            <button onClick={() => handleWhatsApp(app.whatsapp)} disabled={!app.whatsapp} className="px-3 py-2 bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366] text-sm font-bold rounded-xl transition-colors disabled:opacity-50 flex items-center gap-1.5" data-id="element-2709">
                              <MessageCircle className="w-4 h-4" data-id="element-2710" /> WhatsApp
                            </button>

                            <div className="flex-1" data-id="element-2711" />

                            {app.status === 'pending' && <button onClick={() => updateStatus(app.id, 'contacted')} className="px-3 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-sm font-bold rounded-xl transition-colors" data-id="element-2712">
                                Contactado
                              </button>}
                            {app.status !== 'approved' && <button onClick={() => updateStatus(app.id, 'approved')} className="px-3 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-sm font-bold rounded-xl transition-colors" data-id="element-2713">
                                Aprobar
                              </button>}
                            {app.status !== 'rejected' && <button onClick={() => updateStatus(app.id, 'rejected')} className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm font-bold rounded-xl transition-colors" data-id="element-2714">
                                Rechazar
                              </button>}
                          </div>
                        </div>
                      </div>)}
                  </div>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* ═══ DETAIL MODAL ═══ */}
      {selectedApp && <Modal isOpen={detailOpen} onClose={() => setDetailOpen(false)} title="Detalle de Aplicación" data-id="element-2715">
          <div className="space-y-6" data-id="element-2716">
            <div className="flex items-center justify-between border-b border-white/10 pb-4" data-id="element-2717">
              <div data-id="element-2718">
                <h3 className="text-2xl font-bold text-white" data-id="element-2719">
                  {selectedApp.restaurant_name}
                </h3>
                <p className="text-white/60" data-id="element-2720">{selectedApp.owner_name}</p>
              </div>
              {getStatusBadge(selectedApp.status)}
            </div>

            <div className="grid grid-cols-2 gap-6" data-id="element-2721">
              <div data-id="element-2722">
                <p className="text-white/40 text-sm mb-1" data-id="element-2723">WhatsApp</p>
                <p className="text-white font-medium" data-id="element-2724">{selectedApp.whatsapp}</p>
              </div>
              <div data-id="element-2725">
                <p className="text-white/40 text-sm mb-1" data-id="element-2726">Instagram/Facebook</p>
                <p className="text-white font-medium" data-id="element-2727">
                  {selectedApp.social_handle || '-'}
                </p>
              </div>
              <div data-id="element-2728">
                <p className="text-white/40 text-sm mb-1" data-id="element-2729">Tipo de negocio</p>
                <p className="text-white font-medium" data-id="element-2730">
                  {selectedApp.business_type}
                </p>
              </div>
              <div data-id="element-2731">
                <p className="text-white/40 text-sm mb-1" data-id="element-2732">Fecha de solicitud</p>
                <p className="text-white font-medium" data-id="element-2733">
                  {new Date(selectedApp.created_at).toLocaleString('es-CR')}
                </p>
              </div>
              <div data-id="element-2734">
                <p className="text-white/40 text-sm mb-1" data-id="element-2735">
                  ¿Usa pedidos por WhatsApp?
                </p>
                <p className="text-white font-medium" data-id="element-2736">
                  {selectedApp.uses_whatsapp_orders ? 'Sí' : 'No'}
                </p>
              </div>
              <div data-id="element-2737">
                <p className="text-white/40 text-sm mb-1" data-id="element-2738">
                  ¿Tiene menú digital?
                </p>
                <p className="text-white font-medium" data-id="element-2739">
                  {selectedApp.has_digital_menu ? 'Sí' : 'No'}
                </p>
              </div>
            </div>

            <div data-id="element-2740">
              <p className="text-white/40 text-sm mb-1" data-id="element-2741">Objetivo principal</p>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-id="element-2742">
                <p className="text-white/90" data-id="element-2743">
                  {selectedApp.main_goal || 'No especificado'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-4 border-t border-white/10" data-id="element-2744">
              <button onClick={() => handleWhatsApp(selectedApp.whatsapp)} className="flex-1 py-3 bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors" data-id="element-2745">
                <MessageCircle className="w-5 h-5" data-id="element-2746" /> WhatsApp
              </button>

              {selectedApp.status === 'pending' && <button onClick={() => updateStatus(selectedApp.id, 'contacted')} className="flex-1 py-3 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-xl font-bold transition-colors" data-id="element-2747">
                  Marcar Contactado
                </button>}
              {selectedApp.status !== 'approved' && <button onClick={() => updateStatus(selectedApp.id, 'approved')} className="flex-1 py-3 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded-xl font-bold transition-colors" data-id="element-2748">
                  Aprobar
                </button>}
              {selectedApp.status !== 'rejected' && <button onClick={() => updateStatus(selectedApp.id, 'rejected')} className="flex-1 py-3 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-xl font-bold transition-colors" data-id="element-2749">
                  Rechazar
                </button>}
            </div>
          </div>
        </Modal>}

      {/* Toast Notification */}
      {toast && <div className="fixed bottom-6 right-6 z-[60] animate-in slide-in-from-bottom-5 fade-in duration-300" data-id="element-2750">
          <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl border ${toast.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'} backdrop-blur-md`} data-id="element-2751">
            {toast.type === 'success' ? <CheckCircle2 className="w-5 h-5" data-id="element-2752" /> : <AlertCircle className="w-5 h-5" data-id="element-2753" />}
            <span className="font-bold text-sm" data-id="element-2754">{toast.message}</span>
          </div>
        </div>}
    </div>;
}